'use client';

import React, { Suspense, useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/lib/auth/context';
import {
  listBirthProfiles,
  getBirthProfileCharts,
  createBirthProfile,
  createChart,
  calculateTransientChartApi,
  getChatUsageApi,
  sendChatMessageApi,
  BirthProfileRecord,
  ChartRecord
} from '@/lib/api/client';
import { LocationAutocomplete } from '@/components/quick-calculator/LocationAutocomplete';
import { LocationPreset, POPULAR_LOCATIONS } from '@/lib/location/resolver';
import {
  Bot, Sparkles, Send, Loader2, User, RefreshCw, AlertCircle, CheckCircle2,
  Lock, MessageSquare, PlusCircle, ArrowLeft, Info, HelpCircle
} from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const MAX_QUESTION_LENGTH = 500;

const SUGGESTED_QUESTIONS = [
  "What does my current Dasha period mean for me?",
  "What are my chart's biggest strengths and core talents?",
  "What planetary transits should I focus on this year?"
];

export default function AIAstrologerChatPage() {
  return (
    <Suspense>
      <AIAstrologerChatPageInner />
    </Suspense>
  );
}

function AIAstrologerChatPageInner() {
  const { user, loading: authLoading } = useAuth();
  const searchParams = useSearchParams();
  const rawQ = searchParams.get('q');
  const prefillQuestion = rawQ ? rawQ.trim().slice(0, MAX_QUESTION_LENGTH) : '';

  // Step 1: Chart Selection State
  const [step, setStep] = useState<1 | 2>(1); // 1: Chart Select/Input, 2: Active Chat UI
  const [activeTab, setActiveTab] = useState<'saved' | 'fresh'>('saved');

  // Saved profiles
  const [savedProfiles, setSavedProfiles] = useState<BirthProfileRecord[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Fresh profile form
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('1995-05-15');
  const [birthTime, setBirthTime] = useState('10:30:00');
  const [locationName, setLocationName] = useState('New Delhi, India');
  const [selectedPreset, setSelectedPreset] = useState<LocationPreset>(POPULAR_LOCATIONS[0]);
  const [saveProfile, setSaveProfile] = useState(false);

  // Active Chart & Facts for Chat Session
  const [activeChart, setActiveChart] = useState<ChartRecord | null>(null);
  const [activeFacts, setActiveFacts] = useState<any | null>(null);
  const [profileName, setProfileName] = useState<string>('');
  const [calculatingChart, setCalculatingChart] = useState(false);

  // Chat State (IN-SESSION REACT STATE ONLY)
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputQuestion, setInputQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Usage & Rate Limit State
  const [usageCount, setUsageCount] = useState(0);
  const [limit, setLimit] = useState(3);
  const [remaining, setRemaining] = useState(3);
  const [isLimitReached, setIsLimitReached] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 1. Fetch saved profiles & chat usage on load if logged in
  useEffect(() => {
    if (user) {
      fetchUsage();
      fetchProfiles();
    }
  }, [user]);

  // Scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const fetchUsage = async () => {
    try {
      const res = await getChatUsageApi();
      setUsageCount(res.usageCount);
      setLimit(res.limit);
      setRemaining(res.remaining);
      setIsLimitReached(res.isLimitReached);
    } catch {
      // Ignore usage fetch errors
    }
  };

  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const profiles = await listBirthProfiles();
      setSavedProfiles(profiles);
      if (profiles.length > 0) {
        setSelectedProfileId(profiles[0].id);
      } else {
        setActiveTab('fresh');
      }
    } catch {
      setActiveTab('fresh');
    } finally {
      setLoadingProfiles(false);
    }
  };

  const extractFactsFromChart = (chartRecord: ChartRecord) => {
    const chart = chartRecord.chartOutput;
    const dasha = chart.vimshottari_dasha;
    const currentMaha = dasha?.mahadashas?.[0];
    const currentAntar = currentMaha?.antardashas?.[0];

    return {
      birth: chart.birth_details,
      ascendant: chart.ascendant,
      planets: chart.planets,
      houses: chart.houses,
      nakshatras: chart.nakshatras,
      divisionalCharts: chart.divisional_charts || {},
      dasha: {
        birthNakshatra: dasha?.birth_nakshatra || '',
        currentMahadasha: currentMaha?.lord,
        currentAntardasha: currentAntar?.sub_lord,
        mahadashas: dasha?.mahadashas || []
      },
      metadata: {
        ayanamsa: chart.metadata?.ayanamsa_name || 'Lahiri',
        julianDay: chart.metadata?.julian_day_ut || 0,
        engineVersion: chart.metadata?.engine_version || '0.1.0'
      }
    };
  };

  // Step 1 Submit: Initialize Chart Context
  const handleInitializeChartContext = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCalculatingChart(true);

    try {
      let targetChart: ChartRecord;
      let displayName = 'My Birth Chart';

      if (activeTab === 'saved') {
        if (!selectedProfileId) {
          throw new Error('Please select a saved birth profile');
        }
        const profile = savedProfiles.find((p) => p.id === selectedProfileId);
        displayName = profile ? profile.name : 'Saved Profile';

        // Check or create chart
        const charts = await getBirthProfileCharts(selectedProfileId);
        if (charts.length > 0) {
          targetChart = charts[0];
        } else {
          targetChart = await createChart(selectedProfileId, undefined, 'lahiri');
        }
      } else {
        // Fresh Details
        displayName = name.trim() || 'Custom Profile';
        const input = {
          name: displayName,
          birthDate,
          birthTime,
          latitude: selectedPreset.latitude,
          longitude: selectedPreset.longitude,
          timezone: selectedPreset.timezone,
          locationName: selectedPreset.name,
          ayanamsa: 'lahiri' as const
        };

        if (saveProfile) {
          // Persist if checkbox is checked
          const newProfile = await createBirthProfile(input);
          targetChart = await createChart(newProfile.id, undefined, 'lahiri');
        } else {
          // In-memory transient calculation (never persisted)
          targetChart = await calculateTransientChartApi(input);
        }
      }

      const facts = extractFactsFromChart(targetChart);
      setActiveChart(targetChart);
      setActiveFacts(facts);
      setProfileName(displayName);
      if (prefillQuestion && !inputQuestion) {
        setInputQuestion(prefillQuestion);
      }
      setStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize birth chart context.');
    } finally {
      setCalculatingChart(false);
    }
  };

  // Step 2: Send Chat Message
  const handleSendMessage = async (textToSend?: string) => {
    const question = (textToSend || inputQuestion).trim();
    if (!question || sending || isLimitReached || !activeFacts) return;

    setError(null);
    setInputQuestion('');

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    // Append user message immediately to state
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setSending(true);

    try {
      const historyPayload = nextMessages.slice(0, -1).map((m) => ({
        role: m.role,
        content: m.content
      }));

      const res = await sendChatMessageApi({
        question,
        facts: activeFacts,
        history: historyPayload
      });

      const aiMsg: ChatMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: res.reply,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, aiMsg]);
      setUsageCount(res.usageCount);
      setLimit(res.limit);
      setRemaining(res.remaining);
      if (res.usageCount >= res.limit) {
        setIsLimitReached(true);
      }
    } catch (err: any) {
      if (err.status === 429 || err.message?.includes("today's free messages")) {
        setIsLimitReached(true);
        setError("You've used today's free messages. Come back tomorrow for more, or check back soon as we're working on expanded access.");
      } else {
        setError(err.message || 'Failed to generate AI response. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  // Auth Gate Loading State
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#5ab4ff] animate-spin" />
      </div>
    );
  }

  // 1. ACCESS CONTROL: Logged-Out Gate
  if (!user) {
    return (
      <main className="min-h-screen bg-[#030712] text-white py-16 px-4">
        <div className="max-w-md mx-auto bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-8 text-center shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6">
          <div className="w-16 h-16 bg-[#5ab4ff]/10 border border-[#5ab4ff]/25 rounded-2xl flex items-center justify-center mx-auto text-[#5ab4ff]">
            <Lock className="w-8 h-8 text-[#5ab4ff]" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-['Lexend'] font-semibold text-white">AstroPredict AI Astrologer</h1>
            <p className="text-[#dcecff]/70 text-xs font-['Outfit']">
              Please sign in to start a conversational AI astrology session grounded in your Vedic birth chart facts.
            </p>
          </div>
          <Link
            href={`/login?redirect=${encodeURIComponent('/ai-astrologer' + (prefillQuestion ? `?q=${encodeURIComponent(prefillQuestion)}` : ''))}`}
            className="w-full py-3.5 rounded-full bg-white hover:bg-white/90 text-[#050a24] font-semibold text-xs flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(255,255,255,0.25)] transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff]"
            data-testid="login-gate-btn"
          >
            <span>Sign In to Chat</span>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#030712] text-white py-8 pb-20 sm:pb-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header Banner */}
        <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-7 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-gradient-to-br from-[#5ab4ff]/20 to-[#b9c6ff]/10 border border-[#5ab4ff]/30 rounded-2xl text-[#5ab4ff]">
              <Bot className="w-6 h-6 text-[#5ab4ff]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-['Lexend'] font-semibold text-white">AstroPredict AI Astrologer</h1>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-[#5ab4ff]/15 border border-[#5ab4ff]/30 text-[#5ab4ff]">
                  Live Chat
                </span>
              </div>
              <p className="text-xs text-[#dcecff]/70 mt-0.5 font-['Outfit']">
                Vedic Interpretation Engine grounded strictly in Lahiri ephemeris facts.
              </p>
            </div>
          </div>

          {/* Daily Usage Badge */}
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.05] border border-white/12 text-xs font-mono">
            <span className="text-[#dcecff]/65">Daily Messages:</span>
            <span className={`font-bold ${isLimitReached ? 'text-[#ff9bb0]' : 'text-[#8dffc9]'}`}>
              {usageCount} / {limit}
            </span>
          </div>
        </div>

        {/* STEP 1: CHART CONTEXT SELECTION */}
        {step === 1 && (
          <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6">
            <div className="border-b border-white/10 pb-4">
              <h2 className="text-lg font-['Lexend'] font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#5ab4ff]" />
                <span>Step 1: Select Chart Context</span>
              </h2>
              <p className="text-xs text-[#dcecff]/70 mt-1 font-['Outfit']">
                Choose an existing birth profile or enter birth details for an in-memory session.
              </p>
            </div>

            {/* Toggle Tabs - Segmented Control */}
            <div className="flex rounded-2xl bg-white/[0.04] p-1 border border-white/10">
              <button
                type="button"
                onClick={() => setActiveTab('saved')}
                className={`flex-1 py-2.5 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
                  activeTab === 'saved'
                    ? 'bg-white text-[#050a24] shadow-[0_2px_12px_rgba(255,255,255,0.2)]'
                    : 'text-[#dcecff]/70 hover:text-white'
                }`}
                data-testid="tab-saved-profile"
              >
                Saved Birth Profiles ({savedProfiles.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('fresh')}
                className={`flex-1 py-2.5 text-xs font-semibold rounded-xl transition-all cursor-pointer ${
                  activeTab === 'fresh'
                    ? 'bg-white text-[#050a24] shadow-[0_2px_12px_rgba(255,255,255,0.2)]'
                    : 'text-[#dcecff]/70 hover:text-white'
                }`}
                data-testid="tab-fresh-profile"
              >
                Enter Fresh Details
              </button>
            </div>

            <form onSubmit={handleInitializeChartContext} className="space-y-6">
              {activeTab === 'saved' ? (
                <div className="space-y-4">
                  {loadingProfiles ? (
                    <div className="flex items-center justify-center py-8 text-[#dcecff]/60 text-sm gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-[#5ab4ff]" />
                      <span>Loading saved birth profiles...</span>
                    </div>
                  ) : savedProfiles.length > 0 ? (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-[#dcecff]/80">Select Birth Profile</label>
                      <select
                        value={selectedProfileId}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                        className="h-12 w-full rounded-xl bg-white/[0.04] border border-white/12 px-4 text-xs text-white focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff]"
                        data-testid="select-saved-profile"
                      >
                        {savedProfiles.map((p) => (
                          <option key={p.id} value={p.id} className="bg-[#050a24] text-white">
                            {p.name} ({p.birthDate} {p.birthTime} - {p.locationName || 'Saved Location'})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="p-5 bg-white/[0.03] border border-white/10 rounded-2xl text-center space-y-2">
                      <p className="text-xs text-[#dcecff]/70">No saved birth profiles found in your account.</p>
                      <button
                        type="button"
                        onClick={() => setActiveTab('fresh')}
                        className="text-xs font-semibold text-[#5ab4ff] hover:underline cursor-pointer"
                      >
                        Switch to Enter Fresh Details →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-[#dcecff]/80 block mb-1">Full Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-12 w-full rounded-xl bg-white/[0.04] border border-white/12 px-4 text-xs text-white placeholder-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff]"
                      data-testid="input-fresh-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-[#dcecff]/80 block mb-1">Date of Birth</label>
                      <input
                        type="date"
                        required
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        className="h-12 w-full rounded-xl bg-white/[0.04] border border-white/12 px-4 text-xs text-white placeholder-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff]"
                        data-testid="input-fresh-date"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#dcecff]/80 block mb-1">Time of Birth</label>
                      <input
                        type="time"
                        step="1"
                        required
                        value={birthTime}
                        onChange={(e) => setBirthTime(e.target.value)}
                        className="h-12 w-full rounded-xl bg-white/[0.04] border border-white/12 px-4 text-xs text-white placeholder-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff]"
                        data-testid="input-fresh-time"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-[#dcecff]/80 block mb-1">Place of Birth</label>
                    <LocationAutocomplete
                      value={locationName}
                      onSelectLocation={(preset) => {
                        setSelectedPreset(preset);
                        setLocationName(preset.name);
                      }}
                      onClearLocation={() => setSelectedPreset(null as any)}
                      confirmedPreset={selectedPreset}
                      idPrefix="ai-chat-loc"
                    />
                  </div>

                  {/* CRITICAL CHECKBOX: Save profile optional */}
                  <div className="flex items-center gap-2.5 pt-2">
                    <input
                      type="checkbox"
                      id="saveProfileCheckbox"
                      checked={saveProfile}
                      onChange={(e) => setSaveProfile(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#5ab4ff] focus:ring-[#5ab4ff]"
                      data-testid="checkbox-save-profile"
                    />
                    <label htmlFor="saveProfileCheckbox" className="text-xs text-[#dcecff]/75 cursor-pointer">
                      Save this birth profile to my account for future calculator sessions
                    </label>
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3.5 rounded-xl bg-[#ff9bb0]/10 border border-[#ff9bb0]/30 text-[#ff9bb0] text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={calculatingChart || (activeTab === 'saved' && !selectedProfileId)}
                className="w-full py-3.5 rounded-full bg-white hover:bg-white/90 disabled:opacity-50 text-[#050a24] font-semibold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-[0_4px_20px_rgba(255,255,255,0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
                data-testid="start-chat-session-btn"
              >
                {calculatingChart ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-[#050a24]" />
                    <span>Calculating Vedic Chart Facts...</span>
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 text-[#050a24]" />
                    <span>Start Chat Session</span>
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* STEP 2 & 3: SUGGESTIONS AND ACTIVE CHAT INTERFACE */}
        {step === 2 && (
          <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl flex flex-col h-[650px] overflow-hidden">
            {/* Active Session Sub-Header */}
            <div className="px-6 py-3.5 bg-white/[0.02] border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#8dffc9] animate-pulse" />
                <span className="text-xs font-medium text-[#dcecff]/80">
                  Active Context: <span className="text-white font-semibold">{profileName}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-[#5ab4ff] hover:text-[#b9c6ff] flex items-center gap-1.5 transition-colors cursor-pointer"
                data-testid="change-chart-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Change Chart Context</span>
              </button>
            </div>

            {/* Suggested Questions Banner (Step 2) */}
            {messages.length === 0 && (
              <div className="p-6 border-b border-white/10 bg-white/[0.01] space-y-3">
                <div className="flex items-center gap-1.5 text-[10px] text-[#5ab4ff] font-bold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-[#5ab4ff]" />
                  <span>Suggested Questions</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {SUGGESTED_QUESTIONS.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(q)}
                      disabled={sending || isLimitReached}
                      className="p-3.5 text-left rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#5ab4ff]/50 text-xs text-[#dcecff]/75 hover:text-white transition-all group cursor-pointer"
                      data-testid={`suggested-q-${idx}`}
                    >
                      <span className="font-['Outfit']">{q}</span>
                      <span className="block mt-2 text-[10px] text-[#5ab4ff] font-semibold group-hover:translate-x-1 transition-transform">
                        Ask this →
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Scroll Window (Step 3) */}
            <div className="flex-1 p-6 overflow-y-auto space-y-4">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#dcecff]/60 space-y-2">
                  <Bot className="w-10 h-10 text-[#5ab4ff] mb-2" />
                  <p className="text-sm font-medium text-white font-['Lexend']">Ask any question about your chart placements, dashas, or transits.</p>
                  <p className="text-xs text-[#dcecff]/60 font-['Outfit']">In-session memory only. Messages disappear when you close this page.</p>
                </div>
              )}

              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 text-xs shadow-md backdrop-blur-md ${
                      m.role === 'user'
                        ? 'bg-[#5ab4ff]/20 border border-[#5ab4ff]/35 text-white rounded-tr-sm'
                        : 'bg-white/[0.05] border border-white/12 text-[#dcecff] rounded-tl-sm space-y-2'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5 border-b border-white/10 pb-1.5 text-[10px] opacity-80">
                      {m.role === 'user' ? (
                        <User className="w-3 h-3 text-[#5ab4ff]" />
                      ) : (
                        <Bot className="w-3 h-3 text-[#b9c6ff]" />
                      )}
                      <span className="font-semibold text-white">{m.role === 'user' ? 'You' : 'AstroPredict AI'}</span>
                      <span className="ml-auto font-mono text-[9px] text-[#dcecff]/60">{m.timestamp}</span>
                    </div>
                    {m.role === 'user' ? (
                      <div className="whitespace-pre-wrap leading-relaxed font-['Outfit']">
                        {m.content}
                      </div>
                    ) : (
                      <div className="leading-relaxed font-['Outfit'] space-y-2 text-[#dcecff]">
                        <ReactMarkdown
                          components={{
                            h1: ({ children }) => <h3 className="text-sm font-semibold font-['Lexend'] text-white mt-3 mb-1">{children}</h3>,
                            h2: ({ children }) => <h4 className="text-xs font-semibold font-['Lexend'] text-white mt-2 mb-1">{children}</h4>,
                            h3: ({ children }) => <h5 className="text-xs font-semibold font-['Lexend'] text-white mt-2 mb-1">{children}</h5>,
                            p: ({ children }) => <p className="mb-2 leading-relaxed text-[#dcecff]/90">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1 text-[#dcecff]/90">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1 text-[#dcecff]/90">{children}</ol>,
                            li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                            code: ({ children }) => <code className="px-1.5 py-0.5 rounded bg-white/10 text-[#5ab4ff] font-mono text-[11px]">{children}</code>
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex items-start gap-2 text-xs">
                  <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/12 flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#5ab4ff] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-[#5ab4ff] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-[#5ab4ff] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-[#dcecff]/70 font-['Outfit'] ml-1.5">Analyzing chart facts & generating response...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Rate Limit Wall Notification */}
            {isLimitReached && (
              <div className="p-4 bg-[#ff9bb0]/10 border-t border-b border-[#ff9bb0]/30 text-[#ff9bb0] text-xs text-center space-y-1">
                <div className="font-bold text-xs flex items-center justify-center gap-1.5 text-[#ff9bb0]">
                  <AlertCircle className="w-4 h-4" />
                  <span>Daily Free Message Limit Reached</span>
                </div>
                <p className="font-['Outfit']">
                  You've used today's free messages. Come back tomorrow for more, or check back soon as we're working on expanded access.
                </p>
              </div>
            )}

            {error && !isLimitReached && (
              <div className="p-3 bg-[#ff9bb0]/10 border-t border-[#ff9bb0]/25 text-[#ff9bb0] text-xs flex items-center gap-2 px-6">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Input Bar 56px */}
            <div className="p-3 sm:p-4 bg-white/[0.02] border-t border-white/10">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex items-center gap-3"
              >
                <input
                  type="text"
                  disabled={sending || isLimitReached}
                  maxLength={MAX_QUESTION_LENGTH}
                  placeholder={
                    isLimitReached
                      ? "Limit reached. Come back tomorrow for more free messages!"
                      : "Type your question about your Vedic chart..."
                  }
                  value={inputQuestion}
                  onChange={(e) => setInputQuestion(e.target.value)}
                  className="h-14 flex-1 bg-white/[0.04] border border-white/12 rounded-full px-5 text-xs sm:text-sm text-white placeholder-[#dcecff]/40 focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] disabled:opacity-50"
                  data-testid="input-chat-message"
                />
                <button
                  type="submit"
                  disabled={sending || !inputQuestion.trim() || isLimitReached}
                  className="h-12 px-6 rounded-full bg-white hover:bg-white/90 text-[#050a24] font-semibold text-xs flex items-center gap-1.5 disabled:opacity-50 transition-all shadow-[0_2px_12px_rgba(255,255,255,0.25)] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
                  data-testid="btn-send-message"
                >
                  <Send className="w-4 h-4 text-[#050a24]" />
                  <span className="hidden sm:inline">Send</span>
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
